import type { PlayerSettings } from '../saves/types';

type SfxKind = 'token' | 'damage' | 'ability' | 'checkpoint' | 'chase' | 'complete';

const sfx: Record<SfxKind, { frequency: number; end: number; duration: number; type: OscillatorType }> = {
  token: { frequency: 740, end: 980, duration: 0.1, type: 'square' },
  damage: { frequency: 160, end: 70, duration: 0.2, type: 'sawtooth' },
  ability: { frequency: 280, end: 660, duration: 0.22, type: 'square' },
  checkpoint: { frequency: 420, end: 840, duration: 0.34, type: 'triangle' },
  chase: { frequency: 110, end: 160, duration: 0.4, type: 'sawtooth' },
  complete: { frequency: 392, end: 784, duration: 0.6, type: 'triangle' },
};

export class ProceduralAudio {
  private context: AudioContext | null = null;
  private settings: PlayerSettings | null = null;
  private musicTimer: number | null = null;
  private step = 0;

  configure(settings: PlayerSettings): void {
    this.settings = settings;
    if (!settings.music) this.stopMusic();
  }

  async startMusic(settings: PlayerSettings): Promise<void> {
    this.configure(settings);
    if (!settings.music) return;
    const context = this.getContext();
    if (context.state === 'suspended') await context.resume();
    if (this.musicTimer !== null) return;
    const notes = [110, 147, 165, 147, 123, 165, 196, 165];
    this.musicTimer = window.setInterval(() => {
      if (!this.settings?.music) return;
      const frequency = notes[this.step % notes.length];
      this.tone(frequency, frequency * 0.98, 0.12, 'triangle', 0.025);
      if (this.step % 2 === 0) this.tone(62, 48, 0.08, 'sine', 0.04);
      this.step += 1;
    }, 330);
  }

  stopMusic(): void {
    if (this.musicTimer !== null) window.clearInterval(this.musicTimer);
    this.musicTimer = null;
    this.step = 0;
  }

  play(kind: SfxKind): void {
    if (!this.settings?.sound) return;
    const cue = sfx[kind];
    this.tone(cue.frequency, cue.end, cue.duration, cue.type, 0.055);
  }

  private getContext(): AudioContext {
    this.context ??= new AudioContext();
    return this.context;
  }

  private tone(start: number, end: number, duration: number, type: OscillatorType, volume: number): void {
    const context = this.getContext();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(start, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(30, end), context.currentTime + duration);
    gain.gain.setValueAtTime(volume, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, context.currentTime + duration);
    oscillator.connect(gain).connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
  }
}

export const proceduralAudio = new ProceduralAudio();
